import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../../common/reducers/initContext";
import { dataHookWithCredentialsFactory, DefaultFailureAction, DefaultSuccessAction, initMapFactory, initMultiMapFactory, queryMapFactory, readonlyHookWithCredentialsFactory } from "../../common/reducers/utilities";
import { ensureArray } from "../../common/utils/arrayUtilities";
import { useMemo } from "react";
import { ensureInteger } from "../../common/utils/generalUtils";
import { aggregatingFetch } from "../../common/hooks/networkHook";
import { markFetched, registerEntityBasedFetch } from "../../common/hooks/initHook";

const prepareArray = data => ensureArray(data).map(member => ({
    ...member,
    name: member.preferredname ?? member.firstname,
    fullname: (member.preferredname ?? member.firstname) + ' ' + member.lastname,
    // TODO remove once customtables is deprecated needed for ACES computed fields like classification
    customfields: { ...member.customfields, ...member.customtables },
}))


export const memberStaffSlice = createSlice({
    name: 'staffMembers',
    initialState: {
        data: {},
        inactiveMembers: {},
        selfPicture: null,
        selfData: {}
    },
    reducers: {
        setActive: (state, action) => {
            // we can do the following because we always fetch all the members
            state.data = {};
            prepareArray(action.payload).forEach(m => state.data[m.id] = m);
        },
        setInactive : (state, action) => {
            state.inactiveMembers = {};
            prepareArray(action.payload).forEach(m => state.inactiveMembers[m.id] = m);
        },
        set: (state, action) => {
            const toProcess = new Set(action.payload.ids.map(id => ensureInteger(id)));
            prepareArray(action.payload.data).forEach(m => {
                const id = ensureInteger(m.id)
                if (m.inactive) {
                    delete state.data[id];
                    state.inactiveMembers[id] = m;
                } else {
                    state.data[id] = m;
                    delete state.inactiveMembers[id];
                }
                toProcess.delete(m.id);
            });
            toProcess.forEach(id => {
                delete state.data[id];
                delete state.inactiveMembers[id];
            });
        },
        addOrUpdate: (state, action) => {
            prepareArray(action.payload).forEach(m => {
                const id = ensureInteger(m.id);
                if (m.inactive) {
                    delete state.data[id];
                    state.inactiveMembers[id] = m;
                } else {
                    state.data[id] = m;
                    delete state.inactiveMembers[id];
                }
            })
        },
        addOrUpdatePicture: (state, action) => {
            const updates = ensureArray(action.payload);
            state.selfPicture = updates[updates.length - 1];
        },
        addOrUpdateSelfData: (state, action) => {
            const updates = ensureArray(action.payload);
            state.selfData = updates[updates.length - 1];
        },
        removeSelfData: (state, action) => {
            state.selfData = null;
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(m => {
                const id = ensureInteger(m.id);
                if (state.data[id] && m.inactive) {
                    delete state.data[id];
                    state.inactiveMembers[id] = m
                }
            })
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => memberStaffSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'staff_one', action, data => ({ name: `${data.firstname} ${data.lastname}`,
    id: data.id 
}));
const visualizeFailure = verb => DefaultFailureAction(verb, 'staff_one');

const fetchPicture = id => network => network.get(`/api/v2/memberPicture/${encodeURIComponent(id)}`, memberStaffSlice.actions.addOrUpdatePicture, visualizeFailure('get'));
const fetchSelf = network => network.get('/api/v2/selfMember', memberStaffSlice.actions.addOrUpdateSelfData, visualizeFailure('get'));
const fetchAllActiveMembers = network => network.get('/api/v2/staffMembers/active', memberStaffSlice.actions.setActive, visualizeFailure('get'));
const fetchAllInactiveMembers = network => network.get('/api/v2/staffMembers/inactive' , memberStaffSlice.actions.setInactive, visualizeFailure('get')); 
const fetchMember = aggregatingFetch(
    id => network => network.post('/api/v2/staffMembers', [id], data => memberStaffSlice.actions.set({ data, ids: [id] }), visualizeFailure('get')),
    ids => network => network.post('/api/v2/staffMembers', Array.from(ids), data => memberStaffSlice.actions.set({ data, ids }), visualizeFailure('get')),
    { primitiveKey: true }
);
// #endregion

// #region selectors
const selectMemberData = state => state.staffMembers.data;
const selectAllMemberData = state => state.staffMembers;
const selectMembersById = ids => createSelector([selectAllMemberData], data => ids.reduce((res, id) => { let m = data.data[id] ?? data.inactiveMembers[id]; if (m) { res.push(m) } return res }, []));
const selectMemberById = id => createSelector([selectAllMemberData], data => data.data[id] ?? data.inactiveMembers[id]);
const selectAllActiveMembers = createSelector([selectMemberData], (members) => Object.values(members));
const selectInactiveMemberData = state => state.staffMembers.inactiveMembers;
const selectAllInactiveMembers = createSelector([selectInactiveMemberData], (members) => Object.values(members));

const selectAllActiveMembersMap = createSelector([selectMemberData], data => data);
const selectMemberMapById = ids => createSelector([selectMemberData], data => {
    return ids.reduce((acc, id) => {
        const memberData = data[id]
        if (memberData) {
            acc[id] = memberData
        } 
        return acc;
    }, {});
});

const selectSelfPicture = state => state.staffMembers.selfPicture;
const selectSelfData = state => state.staffMembers.selfData;

// #endregion

const listen = {
    memberStaffCreated: (member, { dispatch, websocket }) => {
        markFetched(websocket, 'member', member.id);
        dispatch(memberStaffSlice.actions.addOrUpdate(member))
    },
    memberStaffUpdated: (member, { dispatch }) => dispatch(memberStaffSlice.actions.addOrUpdate(member)),
    memberStaffRemoved: (member, { dispatch }) => dispatch(memberStaffSlice.actions.remove(member)),
    memberSelfUpdated: (member, { dispatch }) => dispatch(memberStaffSlice.actions.addOrUpdateSelfData(member)),
}

const selfListen = {
    memberSelfUpdated: (member, { dispatch }) => dispatch(memberStaffSlice.actions.addOrUpdateSelfData(member)),
}

const pictureListen = {
    memberSelfPictureUpdated: (data, { dispatch }) => dispatch(memberStaffSlice.actions.addOrUpdatePicture(data))
}

// #endregion

const options = { condition: credentials => credentials?.hasRole('member') };
const singleOptions = { ...options, defaultValue: null}

const initMemberByID = initMapFactory(fetchMember, listen);
registerEntityBasedFetch('member', initMemberByID);

const initMultiMemberByID = initMultiMapFactory(initMemberByID, listen);
const initActiveMembers = queryMapFactory(fetchAllActiveMembers, 'id', initMemberByID, listen, { allowEmpty: true, name: 'activeStaffMembers', })();
const initInactiveMembers = queryMapFactory(fetchAllInactiveMembers, 'id', initMemberByID, listen, { allowEmpty: true, name: 'inactiveStaffMewmbers'})();
const initPicture = { run: fetchPicture, listen: pictureListen, immortal: true };
const initSelfData = { run: fetchSelf, listen: selfListen, immortal: true };
export const [useStaffAllActiveMembers] = dataHookWithCredentialsFactory(selectAllActiveMembers, initActiveMembers, options);
export const [useStaffAllInactiveMembers] = dataHookWithCredentialsFactory(selectAllInactiveMembers, initInactiveMembers, options);
export const [useStaffMembers] = dataHookWithCredentialsFactory(selectMembersById, initMultiMemberByID, options);
export const [useStaffMember, realizeStaffMember] = dataHookWithCredentialsFactory(selectMemberById, initMemberByID, singleOptions);
export const [useSelfPicture] = dataHookWithCredentialsFactory(selectSelfPicture, id => initPicture, singleOptions);
export const [useSelfData] = dataHookWithCredentialsFactory(selectSelfData, initSelfData, singleOptions);
export const [useStaffMembersMap] = dataHookWithCredentialsFactory(selectMemberMapById, initMultiMapFactory(initMemberByID, listen, options));
export const [useStaffAllActiveMemberMap] = dataHookWithCredentialsFactory(selectAllActiveMembersMap, initActiveMembers, options);

export const useAllMembers = () => {
    const activeMembers = useStaffAllActiveMembers();
    const inactiveMembers = useStaffAllInactiveMembers();

    return useMemo(() => [...activeMembers, ...inactiveMembers], [activeMembers, inactiveMembers]);
}

export const [useReadStaffMember] = readonlyHookWithCredentialsFactory(selectMemberById, initMemberByID, singleOptions);
export const [useReadStaffMembers] = readonlyHookWithCredentialsFactory(selectMembersById, initMultiMemberByID, options);
export const [useReadStaffAllActiveMembers] = readonlyHookWithCredentialsFactory(selectAllActiveMembers, initActiveMembers, options);
export const [useReadStaffAllActiveMembersMap] = readonlyHookWithCredentialsFactory(selectAllActiveMembersMap, initActiveMembers, options);

export const canStaffUpdateMember = (credentials, member) => credentials.memberID === member.id;

const reducer = { name: memberStaffSlice.name, reducer: memberStaffSlice.reducer }
export default reducer;